/**
 * Public bundle entry point.
 *
 * # Sentry (M+5)
 *
 * `initSentry()` runs BEFORE `createRoot` so the SDK is installed in
 * time to catch errors raised during the initial render. The call is a
 * no-op when `VITE_SENTRY_DSN` is unset — local dev + CI stay quiet.
 *
 * `<Sentry.ErrorBoundary>` wraps the React tree so any uncaught render
 * exception falls back to `ErrorFallback` (brand-aligned) instead of
 * showing a blank page. When Sentry is enabled the eventId surfaces in
 * the fallback so customers can quote it; when disabled the boundary
 * still works, the eventId is just absent.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App.tsx';
import { ErrorFallback } from './components/ErrorFallback';
import { initSentry } from './lib/sentry';
import './index.css';

// Initialise Sentry before any user code runs. The call is idempotent
// and short-circuits when no DSN is set, so it costs nothing in dev.
initSentry();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, componentStack, eventId, resetError }) => (
        <ErrorFallback
          error={error}
          {...(componentStack ? { componentStack } : {})}
          {...(eventId ? { eventId } : {})}
          {...(resetError ? { resetError } : {})}
        />
      )}
    >
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
